import { Capacitor } from '@capacitor/core';
import { InAppReview } from '@capacitor-community/in-app-review';
import { supabase } from '@/lib/supabase';
import { getAppVersion } from '@/utils/appVersion';

export type CriterionRating = 'bad' | 'neutral' | 'good' | null;

export interface FeedbackPayload {
  email: string;
  ratings: {
    usability: CriterionRating;
    design: CriterionRating;
    features: CriterionRating;
    utility: CriterionRating;
  };
  message: string | null;
  app_version: string;
  platform: 'ios' | 'android' | 'web';
  locale: 'fr' | 'en';
}

/** Sujets proposés par « Nous contacter » (mêmes valeurs côté edge function). */
export const CONTACT_TOPICS = ['question', 'bug', 'billing', 'report', 'other'] as const;
export type ContactTopic = typeof CONTACT_TOPICS[number];

/**
 * « Nous contacter » (HARMONIA-54) : même fonction que l'avis, sans les quatre
 * notes, avec un sujet et un message obligatoire.
 */
export interface ContactPayload {
  kind: 'contact';
  topic: ContactTopic;
  email: string;
  message: string;
  app_version: string;
  platform: 'ios' | 'android' | 'web';
  locale: string;
}

export interface FeedbackSubmitResult {
  ok: boolean;
  error?: 'rate_limit' | 'email_failed' | 'unauthorized' | 'network';
}

/** Déclencheurs autorisés de la carte de notation native (TW #17712331).
 *  Le tri ne se fait plus sur ce que les gens répondent (interdit par les
 *  deux stores) mais sur QUI on sollicite et QUAND. */
export type ReviewPromptTrigger =
  | 'reward_delivered'
  | 'streak_chest_opened'
  | 'return_after_absence';

const DAY_MS = 24 * 60 * 60 * 1000;

// Quotas alignés sur les stores : Apple affiche ~3 cartes par utilisateur et
// par an (et peut ne rien afficher du tout), Play applique un quota temporel
// non documenté. Peu de cartouches par foyer : un mauvais déclencheur ne coûte
// pas un avis négatif, il coûte l'occasion de l'année.
const MAX_PROMPTS_PER_YEAR = 3;
const MIN_DAYS_BETWEEN_PROMPTS = 60;

// Le prompt n'apparaît pas avant que le compte ait au moins 24h : on ne
// demande pas d'avis à quelqu'un qui vient de s'inscrire (premier lancement
// et onboarding exclus de fait).
const MIN_ACCOUNT_AGE_MS = 24 * 60 * 60 * 1000;

// Retour dans l'app après une vraie absence = signal de rétention réelle.
const ABSENCE_TRIGGER_DAYS = 3;
// Ce déclencheur tombe au lancement : on laisse l'app se poser avant la carte.
const RETURN_PROMPT_DELAY_MS = 5000;

// Jamais de sollicitation sur les flux d'entrée (onboarding, jonction de
// foyer, auth, deep links) — moments d'échec ou de découverte, pas d'adhésion.
const EXCLUDED_ROUTE_PREFIXES = ['/onboarding', '/join', '/auth', '/go'];

class FeedbackService {
  private userId: string | null = null;
  private accountCreatedAt: number | null = null;
  // Un seul prompt système par session, tous types confondus (carte de
  // notation OU permission notifs) — le composable notifs appelle
  // markSystemPromptShown().
  private systemPromptShownThisSession = false;
  private reviewFlowInFlight = false;

  initializeForUser(userId: string, accountCreatedAt?: string | null) {
    this.userId = userId;
    const ts = accountCreatedAt ? new Date(accountCreatedAt).getTime() : NaN;
    this.accountCreatedAt = Number.isNaN(ts) ? null : ts;
    // L'init auth est aussi le point de passage du démarrage à froid : c'est
    // ici qu'on détecte un retour après absence (le resume natif passe par
    // recordAppActive() via app-resume.service).
    this.recordAppActive();
  }

  resetForCurrentUser() {
    this.userId = null;
    this.accountCreatedAt = null;
    this.systemPromptShownThisSession = false;
  }

  markSystemPromptShown() {
    this.systemPromptShownThisSession = true;
  }

  get promptShownThisSession() {
    return this.systemPromptShownThisSession;
  }

  /**
   * Déclencheur principal : une récompense vient d'être livrée (célébration
   * `reward` côté parent). La promesse du produit vient de se réaliser et
   * c'est le parent qui est devant l'écran — un enfant ne peut pas laisser
   * d'avis. L'appelant séquence l'UI (attendre la fermeture de la modale de
   * célébration) avant d'appeler.
   */
  recordRewardDelivered(): void {
    void this.maybeRequestReview('reward_delivered');
  }

  /** Déclencheur secondaire : ouverture d'un coffre de série (streak ≥ 3 jours). */
  recordStreakChestOpened(): void {
    void this.maybeRequestReview('streak_chest_opened');
  }

  /**
   * À appeler à chaque retour de l'app au premier plan (et à l'init auth).
   * Mémorise le passage et déclenche la sollicitation si l'absence dépasse
   * ABSENCE_TRIGGER_DAYS. Le timestamp est réécrit AVANT le déclenchement :
   * un resume en rafale ne compte qu'une fois.
   */
  recordAppActive(): void {
    if (!this.userId) return;
    const key = this.lastActiveKey;
    const prev = localStorage.getItem(key);
    const now = Date.now();
    localStorage.setItem(key, new Date(now).toISOString());
    if (!prev) return;
    const gapMs = now - new Date(prev).getTime();
    if (Number.isNaN(gapMs)) return;
    if (gapMs >= ABSENCE_TRIGGER_DAYS * DAY_MS) {
      void this.maybeRequestReview('return_after_absence', RETURN_PROMPT_DELAY_MS);
    }
  }

  /**
   * Chemin unique vers la carte de notation native. Aucune question n'est
   * posée avant ni pendant (règle Apple 1.1.7 + Play In-App Review) : la
   * carte s'affiche telle quelle ou pas du tout. L'API ne dit jamais si elle
   * s'est affichée ni si un avis a été déposé — on compte nos demandes dans
   * review_prompt_events et rien d'autre.
   */
  private async maybeRequestReview(trigger: ReviewPromptTrigger, delayMs = 0): Promise<void> {
    if (!this.userId) return this.devTrace(trigger, 'pas d’utilisateur initialisé');
    if (this.systemPromptShownThisSession) return this.devTrace(trigger, 'prompt système déjà montré cette session (mutex)');
    if (this.reviewFlowInFlight) return this.devTrace(trigger, 'déjà en cours');
    if (!Capacitor.isNativePlatform()) return this.devTrace(trigger, 'pas natif');
    if (this.accountCreatedAt !== null &&
        Date.now() - this.accountCreatedAt < MIN_ACCOUNT_AGE_MS) {
      return this.devTrace(trigger, 'compte < 24h');
    }

    this.reviewFlowInFlight = true;
    try {
      if (!(await this.isParentContext())) return this.devTrace(trigger, 'contexte enfant (compte, profil ou vue enfant)');
      if (await this.isExcludedRoute()) return this.devTrace(trigger, 'route exclue');

      // Bypass dev : pas de quota ni d'écriture, pour pouvoir re-déclencher à
      // volonté sur device (la carte iOS s'affiche toujours en build debug).
      const devBypass = import.meta.env.DEV && import.meta.env.MODE !== 'test';
      if (!devBypass && !(await this.passesStoreQuota())) return this.devTrace(trigger, 'quota store atteint ou illisible');
      this.devTrace(trigger, 'GO → requestReview');

      // Cartouche de session réservée avant le délai, pour que le prompt de
      // permission notifs ne s'intercale pas pendant l'attente.
      this.markSystemPromptShown();
      if (delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      // La demande est comptée AVANT l'appel API : si on ne peut pas la
      // compter, on ne sollicite pas (sinon le quota devient invérifiable).
      if (!devBypass && !(await this.recordPromptEvent(trigger))) return;

      await InAppReview.requestReview();

      const { useAnalytics } = await import('@/composables/useAnalytics');
      void useAnalytics().logEvent('feedback_app_store_prompted', {
        platform: Capacitor.getPlatform(),
        trigger,
      });
    } catch (err) {
      console.warn('[FeedbackService] review prompt failed', err);
    } finally {
      this.reviewFlowInFlight = false;
    }
  }

  /** Trace dev uniquement : dit quelle garde a bloqué (ou laissé passer) un
   *  déclencheur — lisible dans l'inspecteur Safari branché sur le device. */
  private devTrace(trigger: ReviewPromptTrigger, reason: string): void {
    if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
      console.info(`[FeedbackService] review(${trigger}) : ${reason}`);
    }
  }

  /** Parent devant l'écran uniquement : ni compte enfant, ni mode enfant. */
  private async isParentContext(): Promise<boolean> {
    try {
      const [{ useChildAuthStore }, { useUserContextStore }, { useAppContextStore }] =
        await Promise.all([
          import('@/stores/childAuth'),
          import('@/stores/userContext'),
          import('@/stores/appContext'),
        ]);
      if (useChildAuthStore().isAuthenticated) return false;
      if (useUserContextStore().isChild) return false;
      if (useAppContextStore().childViewMode) return false;
      return true;
    } catch {
      return false;
    }
  }

  private async isExcludedRoute(): Promise<boolean> {
    try {
      const { default: router } = await import('@/router');
      const path = router.currentRoute.value.path;
      return EXCLUDED_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
    } catch {
      return false;
    }
  }

  private async passesStoreQuota(): Promise<boolean> {
    const yearAgo = new Date(Date.now() - 365 * DAY_MS).toISOString();
    const { data, error } = await supabase
      .from('review_prompt_events')
      .select('created_at')
      .eq('user_id', this.userId!)
      .gte('created_at', yearAgo)
      .order('created_at', { ascending: false });

    // Réseau/DB en échec → on renonce : mieux vaut rater une occasion que
    // solliciter sans pouvoir la compter.
    if (error || !data) return false;
    if (data.length >= MAX_PROMPTS_PER_YEAR) return false;

    const lastAt = data[0] ? new Date(data[0].created_at).getTime() : null;
    if (lastAt !== null && Date.now() - lastAt < MIN_DAYS_BETWEEN_PROMPTS * DAY_MS) {
      return false;
    }
    return true;
  }

  private async recordPromptEvent(trigger: ReviewPromptTrigger): Promise<boolean> {
    const { error } = await supabase.from('review_prompt_events').insert({
      user_id: this.userId!,
      source: trigger,
      app_version: await getAppVersion(),
    });
    if (error) {
      console.warn('[FeedbackService] recordPromptEvent failed', error);
      return false;
    }
    return true;
  }

  submitNegativeFeedback(payload: FeedbackPayload): Promise<FeedbackSubmitResult> {
    return this.sendToSupport(payload);
  }

  submitContactMessage(payload: ContactPayload): Promise<FeedbackSubmitResult> {
    return this.sendToSupport(payload);
  }

  private async sendToSupport(payload: FeedbackPayload | ContactPayload): Promise<FeedbackSubmitResult> {
    if (!this.userId) {
      return { ok: false, error: 'unauthorized' };
    }

    try {
      const { data, error } = await supabase.functions.invoke('send-feedback-email-mailtrap', {
        body: payload,
      });

      if (error) {
        // Supabase wraps non-2xx responses in FunctionsHttpError; the actual
        // server body lives on error.context (a Response). Extract it so we
        // surface the real reason (401 unauthorized / 400 invalid_payload /
        // 429 rate_limit / 500 email_failed) instead of a generic "network".
        const { status, body } = await readFunctionError(error);
        console.warn('[FeedbackService] sendToSupport invoke error', { status, body, error });

        if (status === 429 || body?.error === 'rate_limit') {
          return { ok: false, error: 'rate_limit' };
        }
        if (status === 401 || body?.error === 'unauthorized') {
          return { ok: false, error: 'unauthorized' };
        }
        if (body?.error === 'email_failed') {
          return { ok: false, error: 'email_failed' };
        }
        return { ok: false, error: 'network' };
      }

      if (!data?.ok) {
        const code = (data?.error as FeedbackSubmitResult['error']) ?? 'email_failed';
        return { ok: false, error: code };
      }

      return { ok: true };
    } catch (err) {
      console.warn('[FeedbackService] sendToSupport threw', err);
      return { ok: false, error: 'network' };
    }
  }

  private get lastActiveKey() {
    return `harmonia_last_active_${this.userId}`;
  }
}

export const feedbackService = new FeedbackService();

async function readFunctionError(err: unknown): Promise<{ status: number | null; body: any }> {
  const ctx = (err as { context?: Response }).context;
  if (!ctx || typeof ctx !== 'object') return { status: null, body: null };
  const status = typeof ctx.status === 'number' ? ctx.status : null;
  try {
    const text = await ctx.text();
    try { return { status, body: JSON.parse(text) }; }
    catch { return { status, body: text }; }
  } catch {
    return { status, body: null };
  }
}
